import { computed, type Ref } from 'vue'

import { recordLoadError } from './recordLoadError'

interface LoadableQuery {
  isLoading: Ref<boolean>
  isFetching: Ref<boolean>
  isError: Ref<boolean>
  error: Ref<unknown>
  refetch: () => unknown
}

/**
 * The four things a panel, card or chart hands `LoadErrorAlert` (or `SummaryCard`) about the
 * queries behind it: `isLoading`, `loadError`, `isRetrying` and `retry`. Several queries are
 * one block — loading while any is, the first refusal's sentence, and `retry` refetches only
 * the ones that failed.
 *
 * `message` is the fallback when the server sent no sentence of its own. An edit modal does
 * not need this: `recordLoadError` feeds `FormModal`'s `load-error` directly.
 */
export function useLoadState(message: string, ...queries: LoadableQuery[]) {
  const errors = queries.map((query) =>
    recordLoadError(query.isError, query.error, message)
  )

  function retry() {
    for (const query of queries) if (query.isError.value) void query.refetch()
  }

  return {
    isLoading: computed(() => queries.some((query) => query.isLoading.value)),
    loadError: computed(() => errors.map((error) => error.value).find(Boolean)),
    // A refetch after an error leaves `isLoading` false; this is what shows "Try again" working.
    isRetrying: computed(() => queries.some((query) => query.isFetching.value)),
    retry
  }
}
